import { isFilterGroup } from './filters'
import { type FilterGroup, type ViewConfig, type ViewGrouping } from '../models'

/** Remove filter leaves for a deleted field, pruning groups left empty. */
export function removeColumnFromFilters(group: FilterGroup, columnId: string): FilterGroup {
  let changed = false
  const conditions: FilterGroup['conditions'] = []

  for (const condition of group.conditions) {
    if (!isFilterGroup(condition)) {
      if (condition.column_id === columnId) {
        changed = true
        continue
      }
      conditions.push(condition)
      continue
    }

    const nextGroup = removeColumnFromFilters(condition, columnId)
    if (nextGroup !== condition) changed = true
    if (nextGroup.conditions.length === 0) {
      changed = true
      continue
    }
    conditions.push(nextGroup)
  }

  return changed ? { ...group, conditions } : group
}

/**
 * Strip a deleted field from grouping, and from the sorts and aggregation
 * overrides owned by the remaining grouping levels.
 */
export function removeColumnFromGrouping(
  grouping: ViewGrouping[],
  columnId: string
): ViewGrouping[] {
  let changed = false
  const nextGrouping: ViewGrouping[] = []

  for (const group of grouping) {
    if (group.column_id === columnId) {
      changed = true
      continue
    }

    const currentOverrides = group.aggregation_overrides ?? []
    const aggregationOverrides = currentOverrides.filter(
      (aggregation) => aggregation.column_id !== columnId
    )
    const sortBy = group.sort_by.filter((sort) => sort.column_id !== columnId)
    if (
      aggregationOverrides.length !== currentOverrides.length ||
      sortBy.length !== group.sort_by.length
    ) {
      changed = true
      nextGrouping.push({
        ...group,
        sort_by: sortBy,
        aggregation_overrides: aggregationOverrides.length > 0 ? aggregationOverrides : undefined,
      })
      continue
    }

    nextGrouping.push(group)
  }

  return changed ? nextGrouping : grouping
}

export function doesColumnAffectRowSemantics(
  config: Pick<ViewConfig, 'filters' | 'sorts' | 'grouping'>,
  columnId: string
): boolean {
  if (removeColumnFromFilters(config.filters, columnId) !== config.filters) return true
  if (config.sorts.some((sort) => sort.column_id === columnId)) return true
  return config.grouping.some(
    (group) =>
      group.column_id === columnId || group.sort_by.some((sort) => sort.column_id === columnId)
  )
}

/**
 * Remove every persisted view reference to a deleted field. Projection-only
 * metadata is deliberately handled here without touching cached record rows.
 */
export function removeColumnFromViewConfig(view: ViewConfig, columnId: string): ViewConfig {
  const columnIds = view.column_ids.filter((id) => id !== columnId)
  const columnConfigs = view.column_configs.filter((config) => config.column_id !== columnId)
  const filters = removeColumnFromFilters(view.filters, columnId)
  const sorts = view.sorts.filter((sort) => sort.column_id !== columnId)
  const groupingColumnRemoved = view.grouping.some((group) => group.column_id === columnId)
  const grouping = removeColumnFromGrouping(view.grouping, columnId)
  const groupStates = groupingColumnRemoved
    ? []
    : view.group_states.filter(
        (state) => !Object.prototype.hasOwnProperty.call(state.group_id, columnId)
      )
  const groupLevelStates = groupingColumnRemoved
    ? []
    : view.group_level_states.filter((state) => state.column_id !== columnId)
  const footerAggregations = view.footer_aggregations.filter(
    (aggregation) => aggregation.column_id !== columnId
  )
  const aggregations = Object.prototype.hasOwnProperty.call(view.aggregations, columnId)
    ? Object.fromEntries(
        Object.entries(view.aggregations).filter(([aggregationColumnId]) => {
          return aggregationColumnId !== columnId
        })
      )
    : view.aggregations

  const changed =
    columnIds.length !== view.column_ids.length ||
    columnConfigs.length !== view.column_configs.length ||
    filters !== view.filters ||
    sorts.length !== view.sorts.length ||
    grouping !== view.grouping ||
    groupStates.length !== view.group_states.length ||
    groupLevelStates.length !== view.group_level_states.length ||
    footerAggregations.length !== view.footer_aggregations.length ||
    aggregations !== view.aggregations

  if (!changed) return view

  return {
    ...view,
    column_ids: columnIds,
    column_configs: columnConfigs,
    filters,
    sorts,
    grouping,
    aggregations,
    // Removing a grouping level invalidates hierarchical expansion paths.
    group_states: groupStates,
    group_level_states: groupLevelStates,
    footer_aggregations: footerAggregations,
  }
}
